import React from "react";
import { Text, UnorderedList, ListItem, Link, VStack } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { LegalPageShell, LegalSection } from "./legal/LegalPageShell";

const TermsAndConditions = () => {
  return (
    <LegalPageShell
      badge="Legal"
      title="Terms & conditions"
      intro="These terms govern your use of Manage360. They are written to be readable: what you can expect from us, what we expect from you, and how disputes are handled."
      lastUpdated="Last updated: February 2026"
    >
      <VStack align="stretch" spacing={10}>
        <LegalSection title="The service">
          <Text mb={4}>
            Manage360 is a software product operated by <strong>Our Software Solutions Pvt. Ltd.</strong>{" "}
            (“we”, “us”). It brings together tools such as notes, files, portfolio-style money tracking,
            projects, todos, daily chores, calendar-related features, and shared group expenses—along with
            optional voice assistance on certain screens.
          </Text>
          <Text>
            By creating an account or using the website or app, you agree to these terms and to our{" "}
            <Link as={RouterLink} to="/privacy-policy" color="teal.500" fontWeight="semibold">
              Privacy Policy
            </Link>
            . If you do not agree, please do not use the service.
          </Text>
        </LegalSection>

        <LegalSection title="Your account">
          <UnorderedList spacing={3} pl={1}>
            <ListItem>You must provide accurate registration information and keep your password or device safe.</ListItem>
            <ListItem>
              You are responsible for all activity under your account until you notify us of unauthorised use.
            </ListItem>
            <ListItem>
              We may suspend or close accounts that violate these terms, pose a security risk, or abuse the
              platform.
            </ListItem>
          </UnorderedList>
        </LegalSection>

        <LegalSection title="Acceptable use">
          <Text mb={4}>You agree not to:</Text>
          <UnorderedList spacing={3} pl={1}>
            <ListItem>Use Manage360 for unlawful, harmful, or deceptive activity.</ListItem>
            <ListItem>Upload malware, scrape the service without permission, or try to break authentication.</ListItem>
            <ListItem>Overload or interfere with our infrastructure or other users’ access.</ListItem>
            <ListItem>
              Store or share content that infringes others’ intellectual property or privacy without rights
              or consent.
            </ListItem>
          </UnorderedList>
        </LegalSection>

        <LegalSection title="Your content">
          <Text mb={4}>
            You keep ownership of content you add (notes, files, numbers you type, tasks, and so on). You give
            us a limited licence to host, process, and display that content strictly to run the service—
            including backups, security scanning, and improvements that do not sell your private data.
          </Text>
          <Text>
            You represent that you have the right to upload or enter what you put in Manage360. We may remove
            content that clearly violates law or these terms.
          </Text>
        </LegalSection>

        <LegalSection title="Feature-specific notes">
          <Text mb={4}>
            Different areas of the app have different jobs. This section does not replace independent
            financial, legal, or tax advice.
          </Text>
          <UnorderedList spacing={3} pl={1}>
            <ListItem>
              <strong>Portfolio / money tracking</strong> — The app helps you record and view information you
              enter. We are not a bank, broker, or financial adviser. You remain responsible for decisions and
              accuracy of your data.
            </ListItem>
            <ListItem>
              <strong>Projects, todos, chores</strong> — Tools for planning and routines. Reminders may use
              contact details you save; delivery depends on carriers and third-party services.
            </ListItem>
            <ListItem>
              <strong>Group expenses</strong> — Features for shared costs and balances. Settlement is between
              you and your group; we provide the workspace, not legal enforcement of debts.
            </ListItem>
            <ListItem>
              <strong>Voice AI</strong> — Optional. Usage may consume AI Usage Credits. Paid packs, if available,
              follow checkout and refund rules shown at purchase.
            </ListItem>
          </UnorderedList>
        </LegalSection>

        <LegalSection title="Third-party services">
          <Text>
            Some features rely on third parties (for example sign-in providers, calendar APIs, messaging, or
            payment gateways). Their terms and privacy policies apply to how they process data on their side.
            We are not responsible for outages or changes made by those providers.
          </Text>
        </LegalSection>

        <LegalSection title="Disclaimers">
          <Text mb={4}>
            The service is provided <strong>“as is”</strong> and <strong>“as available”</strong>. To the
            maximum extent permitted by law, we disclaim implied warranties of merchantability, fitness for a
            particular purpose, and non-infringement.
          </Text>
          <Text>
            We strive for high availability but do not guarantee uninterrupted access. You use Manage360 at
            your own risk regarding decisions you make based on data in the app.
          </Text>
        </LegalSection>

        <LegalSection title="Limitation of liability">
          <Text>
            To the fullest extent permitted by applicable law, our total liability arising from these terms
            or the service—whether in contract, tort, or otherwise—is limited to the amount you paid us for
            Manage360 in the twelve (12) months before the claim (or, if none, a modest fixed cap set by
            applicable law). We are not liable for indirect, incidental, special, consequential, or punitive
            damages, or for lost profits or data—except where law forbids such exclusions.
          </Text>
        </LegalSection>

        <LegalSection title="Indemnity">
          <Text>
            You agree to defend and indemnify us against claims by third parties arising from your misuse of
            the service, your content, or your violation of these terms—subject to our right to control our
            own defence at our expense if we choose.
          </Text>
        </LegalSection>

        <LegalSection title="Governing law & disputes">
          <Text>
            These terms are governed by the laws applicable to Our Software Solutions Pvt. Ltd.’s place
            of business in India, without regard to conflict-of-law rules—unless mandatory consumer laws in
            your country say otherwise. Courts in that jurisdiction generally have exclusive jurisdiction,
            except where you have non-waivable rights to sue locally.
          </Text>
        </LegalSection>

        <LegalSection title="Contact">
          <Text>
            Questions about these terms? Email{" "}
            <Link href="mailto:support@manage360.com" color="teal.500" fontWeight="semibold">
              support@manage360.com
            </Link>
            .
          </Text>
        </LegalSection>

      </VStack>
    </LegalPageShell>
  );
};

export default TermsAndConditions;
